import React from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { ACTIVE_PRODUCT, ADMIN } from '../config/productTarget';

/**
 * THE STUDY-OUTPUT DRAWER'S SUMMON BUTTON — top-right chrome, beside the theme toggle.
 *
 * <h2>⚠ WHY A HEADER CONTROL RATHER THAN A TOOLBAR TOOL</h2>
 *
 * `messages_panel` exists in the tool registry and is named in the ADMIN manifest's System tab
 * (`admin.manifest.js`, Tab 3) and in no customer manifest at all — so for an administrator the
 * panel row is the route, and for an astro or dhanabhyas customer there was no route whatsoever.
 * That is the defect: a customer product has no Admin Panel, so a tool that lives only in one is
 * unreachable, and a reader running a strategy study could not open the drawer holding its output.
 *
 * <p>⚠ IT IS NOT "RELOCATED" IN THE `adminPanelTools` SENSE — checked rather than assumed, because
 * the two look alike. `RELOCATED_ACTIONS` does not contain it, so nothing is hiding it from a
 * toolbar; it is simply absent from the customer allow-lists. Naming the id in those manifests
 * WOULD therefore have put it on the bar — but that is the wrong KIND of control for it: the
 * toolbar is instruments that change the chart, and this opens a panel reporting what an
 * instrument already said. So it joins the chrome, next to the theme toggle and the profile menu,
 * which is where the brief puts it and where a reader looks for a drawer.
 *
 * <p>⚠ THE ADMIN PANEL ROW STAYS. This is a second route to one panel, not a replacement — the
 * System tab entry in `admin.manifest.js` is untouched, so an administrator reaches it both ways.
 *
 * <h2>⚠ IT TOGGLES, where the dispatcher's action SHOWS</h2>
 *
 * `actionDispatcher`'s {@code open_messages_panel} case calls {@code setMessagesOpen(true)} and
 * says why: *"'Show', not 'toggle': the toolbar button it replaced always opened the panel."* That
 * is right for a panel ROW — a reader clicking a row in a dialog means "show me" — and wrong for a
 * persistent chrome button, which the brief asks to "toggle the results drawer open/closed at
 * will". A chrome button that only ever opened would have no way to close what it opened.
 *
 * <p>So this does not dispatch the action; it drives the state directly, and the two semantics
 * coexist without either being compromised.
 *
 * <h2>⚠ ALL FOUR PRODUCTS — integralDelta ADDED 2026-11-03, reversing one day's exclusion</h2>
 *
 * It shipped as astro/dhanabhyas/admin, excluded from integralDelta on the reasoning that the
 * product carries no ephemeris study so the Summary/Journal tabs would be empty, and that its
 * header is deliberately bare (`ActiveOverlayPills` returns null there).
 *
 * <p>That was wrong on its own terms. **integralDelta is the product with the MOST to put in this
 * panel** — the CTS scans, the PTA/PTB cycle runs and the three engine runners all write their
 * output to it, and `handleStudyReportJournal` fills the Journal tab from exactly those. The
 * "empty tabs" premise confused *no ephemeris study* with *no study*.
 *
 * <p>The bare-header argument does not transfer either: that one is about the ACTIVE-OVERLAY bar,
 * which names what is shaded on the chart and which integralDelta asked to have removed. A drawer
 * toggle is chrome, not a running-state readout — it is the same kind of control as the theme
 * toggle beside it, and nobody asked for that to go.
 *
 * <p>Gated here rather than by a manifest, following the precedent of `ActiveOverlayPills`: a
 * header control takes the resolved product as a prop and decides for itself. That keeps the
 * manifests a statement about the TOOLBAR, which is what every test about them assumes.
 *
 * <p>⚠ AND NO ROLE CHECK, deliberately. There is no `isAdmin` anywhere in this file and no route
 * through the Admin Panel: a customer running a study must be able to read its output, and gating
 * the only way to see it on a role is the defect this control was added to fix.
 */

/**
 * THE PRODUCTS WHOSE HEADER CARRIES IT — the Studio alone, as of 2026-11-03.
 *
 * <h2>⚠ IT MOVED DOWN TO THE TOOLBAR; IT WAS NOT DUPLICATED THERE</h2>
 *
 * This read all four for one commit. The three CUSTOMER products now carry the control on the
 * toolbar strip instead, at the far right beside the Bar Floater and the Cycles dialog — so
 * keeping the header copy would leave two buttons for one drawer on one screen, which is what
 * "move it down into the primary toolbar strip" asks not to happen.
 *
 * <p>⚠ ADMIN KEEPS IT, and that is not an oversight. The Studio's toolbar and its Admin Panel are
 * DISJOINT by three invariants in `manifests.test.js`, and `messages_panel` lives in its System
 * tab — so the Studio has no toolbar entry to move the control into. Dropping the header button
 * there would leave the admin build reaching this drawer only through a panel row that
 * deliberately only ever SHOWS, i.e. with no way to close it from a control at all.
 *
 * <p>Kept as an explicit list rather than a predicate, because this is a product decision and a
 * later product should have to be added deliberately.
 */
export const MESSAGES_PANEL_BUTTON_PRODUCTS = Object.freeze([ADMIN]);

/** Whether this product's header shows it. Exported so a test can ask without rendering. */
export function showsMessagesPanelButton(product) {
  return MESSAGES_PANEL_BUTTON_PRODUCTS.includes(product);
}

export function MessagesPanelButton({ open, onToggle, product = ACTIVE_PRODUCT }) {
  if (!showsMessagesPanelButton(product)) return null;

  return (
    <Tooltip title="Toggle Message Panel / Study Results">
      <IconButton
        size="small"
        color="inherit"
        /*
         * ⚠ A STABLE NAME PLUS `aria-pressed`, reversing a flipping label.
         *
         * It read `Hide study output panel` / `Show study output panel`. That is the right
         * pattern for a BUTTON whose action changes; it is the wrong one for a TOGGLE, which is
         * what this is — a screen reader announces the name and the pressed state together, so a
         * flipping name says the state twice and the two spellings disagree about which way round
         * it is ("Show …, pressed"). One name, one state channel.
         */
        aria-label="Toggle Message Panel"
        aria-pressed={Boolean(open)}
        data-testid="messages-panel-button"
        onClick={onToggle}
        sx={{
          /*
           * ⚠ `flexShrink: 0` IS STATED rather than inherited. It is 0 by MUI's own default today,
           * so this changes nothing — and the header's brand is `flexGrow: 1`, so the day a
           * narrower viewport or a longer brand squeezes this row, an inherited default is what
           * silently takes the button to zero width. Measured at a simulated 1440: 30x30, intact.
           */
          flexShrink: 0,
          // Filled when the drawer is open, so the button says which state it is in on screen as
          // well as to a reader. The chrome around it is `color: inherit`, so this is the one
          // thing distinguishing the two.
          bgcolor: open ? 'rgba(255,255,255,0.22)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.30)' },
          // A press has to be felt as well as seen: without this the only feedback on a click is
          // the panel appearing, which on a slow first render reads as nothing having happened.
          '&:active': { bgcolor: 'rgba(255,255,255,0.40)' },
          transition: 'background-color 120ms ease',
        }}
      >
        {/*
          * ⚠ A CLIPBOARD, NOT AN ENVELOPE — and this is a collision, not a preference.
          *
          * The brief offers "envelope/chat/dock". An envelope would conflate this panel
          * (`messages_panel` — study OUTPUT: Summary, Journal, the reserved tabs) with
          * `message_centre`, the operator notice board, which astro's own manifest docblock warns
          * are "routinely referred to by one name". Both exist on ADMIN, side by side, so on one
          * product the two would be indistinguishable.
          *
          * A clipboard says "results" and belongs to neither mail metaphor.
          */}
        <AssignmentOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  );
}

MessagesPanelButton.propTypes = {
  /** Whether the drawer is currently open — drives the icon's pressed state. */
  open: PropTypes.bool,
  /** Called with no arguments on every press. The caller owns the flip. */
  onToggle: PropTypes.func.isRequired,
  /** The resolved build target. Defaults to the baked one, so a caller that omits it still gates. */
  product: PropTypes.string,
};

export default MessagesPanelButton;
